import React, { useState, useEffect, useCallback } from 'react';
import { fetchPendingBlockedSeats, decidePendingBlockedSeats } from '../api/client';
import { NEU_BG, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, BORDER_SUBTLE, COLOR_OK, COLOR_WARN, COLOR_DANGER } from '../theme';
import HallSeatMap from './HallSeatMap';

const btnStyle = {
  padding: '12px 24px',
  borderRadius: 8,
  border: 'none',
  fontSize: 15,
  fontWeight: 600,
  fontFamily: 'Heebo, sans-serif',
  color: '#fff',
  transition: 'all 0.2s',
  cursor: 'pointer',
};

function formatEvidenceDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Central inbox of pending blocked-seat proposals across all halls,
 * with evidence, seat map and inline approve/reject.
 * Mounted on both ScrapePage and AdminPage.
 * showEmpty: render a neutral "no pending" box instead of disappearing.
 */
export default function PendingBlockedApprovals({ showEmpty = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [expandedMap, setExpandedMap] = useState({});

  const loadPending = useCallback(() => {
    fetchPendingBlockedSeats()
      .then((res) => { setItems(res?.items || []); setError(false); })
      .catch(() => { setItems([]); setError(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadPending();
    const timer = setInterval(loadPending, 30000);
    return () => clearInterval(timer);
  }, [loadPending]);

  const decide = async (item, decision) => {
    const key = `${item.cinema_id}::${item.hall}`;
    setBusyKey(key);
    try {
      await decidePendingBlockedSeats(item.cinema_id, item.hall, decision);
      await loadPending();
    } finally {
      setBusyKey('');
    }
  };

  const toggleMap = (key) => setExpandedMap((prev) => ({ ...prev, [key]: !prev[key] }));

  if (loading) return null;
  if (error) {
    return (
      <div style={{ color: COLOR_DANGER, fontSize: 13, marginBottom: 12 }}>
        שגיאה בטעינת הצעות לחסימת כיסאות
      </div>
    );
  }
  if (items.length === 0) {
    if (!showEmpty) return null;
    return (
      <div style={{
        border: `1px solid ${BORDER_SUBTLE}`,
        borderRadius: 12,
        padding: '12px 14px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <span style={{ color: COLOR_OK, fontWeight: 700 }}>✓</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>הצעות לחסימת כיסאות</div>
          <div style={{ fontSize: 12, color: TEXT_TERTIARY }}>
            אין הצעות ממתינות לאישור כרגע — הצעות חדשות יופיעו כאן אוטומטית.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--warn-bg)',
      border: '1px solid rgba(245, 158, 11, 0.45)',
      borderRadius: 12,
      padding: 14,
      marginBottom: 16,
    }}>
      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8, color: COLOR_WARN }}>
        🔔 נדרש אישור לחסימת כיסאות ({items.length})
      </div>
      <div style={{ display: 'grid', gap: 8, maxHeight: '60vh', overflowY: 'auto' }}>
        {items.map((item) => {
          const key = `${item.cinema_id}::${item.hall}`;
          const isBusy = busyKey === key;
          const hasMap = item.all_seat_data && item.all_seat_data.length > 0;
          const mapOpen = expandedMap[key] === true && hasMap;
          return (
            <div key={key} style={{
              border: `1px solid ${BORDER_SUBTLE}`,
              borderRadius: 10,
              padding: '10px 12px',
              background: NEU_BG,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 220 }}>
                  <div style={{ fontWeight: 700 }}>{item.cinema} • {item.hall}</div>
                  <div style={{ color: TEXT_SECONDARY, fontSize: 12 }}>
                    חסומים מאושרים: {item.approved_blocked_count} → מוצע: {item.pending_blocked_count} • סריקות: {item.scan_count}
                  </div>
                  <div style={{ color: TEXT_TERTIARY, fontSize: 12 }}>
                    דלתא: {item.delta_blocked_count > 0 ? `+${item.delta_blocked_count}` : item.delta_blocked_count} • קיבולת: {item.effective_total_seats || '-'}
                  </div>
                  {item.evidence && (
                    <div style={{ color: COLOR_WARN, fontSize: 12, marginTop: 2 }}>
                      {item.proposal_type === 'streak' ? (
                        <>
                          רצף: {item.evidence.streak_len} הקרנות רצופות
                          {item.evidence.first_showtime && item.evidence.last_showtime && (
                            <> ({formatEvidenceDate(item.evidence.first_showtime)}–{formatEvidenceDate(item.evidence.last_showtime)})</>
                          )}
                          {item.evidence.group_size > 1 && <>, {item.evidence.group_size} מושבים זהים</>}
                          {item.evidence.movie_count > 1 && <> • {item.evidence.movie_count} סרטים שונים</>}
                          {Number.isFinite(item.evidence.min_residual_occupancy) && (
                            <> • תפוסה שיורית {Math.round(item.evidence.min_residual_occupancy * 100)}%</>
                          )}
                        </>
                      ) : item.proposal_type === 'frozen' ? (
                        <>
                          מפה קפואה: אותם {item.evidence.group_size || item.pending_blocked_count} מושבים "מכורים" זהים
                          {' '}ב-{item.evidence.observations || item.evidence.window_observations} הקרנות
                          {item.evidence.movie_count > 1 && <> של {item.evidence.movie_count} סרטים שונים</>}
                          {' '}— קהל אמיתי לא קונה כך
                        </>
                      ) : item.proposal_type === 'persistent' ? (
                        <>
                          חסימה מתמשכת: {item.evidence.group_size || item.pending_blocked_count} מושבים שמעולם לא נצפו זמינים
                          {' '}({item.evidence.window_observations} תצפיות, אולם נצפה ריק חוץ מהם ב-{item.evidence.anchor_screenings} הקרנות)
                        </>
                      ) : (
                        <>זיהוי קבוע: מושבים "תפוסים" בהקרנות כמעט ריקות ({item.evidence.window_observations || item.scan_count} תצפיות)</>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {hasMap && (
                    <button
                      onClick={() => toggleMap(key)}
                      style={{ ...btnStyle, background: 'var(--faint)', color: TEXT_SECONDARY, fontSize: 11, padding: '4px 10px' }}
                    >
                      {mapOpen ? 'הסתר מפה ▴' : 'הצג מפה ▾'}
                    </button>
                  )}
                  <button
                    disabled={isBusy}
                    onClick={() => decide(item, 'approve')}
                    style={{ ...btnStyle, fontSize: 13, padding: '7px 16px', background: isBusy ? 'var(--faint)' : COLOR_OK }}
                  >
                    אשר
                  </button>
                  <button
                    disabled={isBusy}
                    onClick={() => decide(item, 'reject')}
                    style={{ ...btnStyle, fontSize: 13, padding: '7px 16px', background: isBusy ? 'var(--faint)' : COLOR_DANGER }}
                  >
                    דחה
                  </button>
                </div>
              </div>
              {mapOpen && (
                <HallSeatMap seatData={item.all_seat_data} />
              )}
              {item.sample_screenings?.length > 0 && (
                <div style={{ marginTop: 8, borderTop: `1px solid ${BORDER_SUBTLE}`, paddingTop: 8 }}>
                  <div style={{ fontSize: 10, color: TEXT_TERTIARY, marginBottom: 4 }}>
                    הקרנות לאימות — בחר ובדוק באתר הרשת ({item.sample_screenings.length}):
                  </div>
                  <select
                    defaultValue=""
                    style={{
                      width: '100%',
                      fontSize: 11,
                      padding: '4px 6px',
                      borderRadius: 6,
                      border: `1px solid ${BORDER_SUBTLE}`,
                      background: 'var(--surf-alt)',
                      color: TEXT_PRIMARY,
                      direction: 'rtl',
                    }}
                  >
                    <option value="" disabled>בחר הקרנה…</option>
                    {item.sample_screenings.map((s, i) => (
                      <option key={i} value={i}>{s.showtime} — {s.movie_title}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
